import { PrivateContext } from "@/routes/PrivateRoute";
import { useContext, useState, useEffect, useSelector } from "@/common/hooks";
import { useParams } from "react-router";
import { ErrorMessage, Spinner, SuccessPopup } from "@/common/components";
import ProductPageOne from "./Common/ProductPageOne";
import ProductPageTwo from "./Common/ProductPageTwo";
import ProductPageThree from "./Common/ProductPageThree";
import { validate } from "@/utils/Validations";
import { useSaveProductDetailsMutation } from "@/service/ProductService";
import { scrollToTop } from "@/utils/Utilities";
import { setProductDetails } from "@/store/productSlice";
import { ProductContext } from "./ViewProductDetails";
import { Constants } from "@/utils/Constants";

const DuplicateProduct = () => {
  const {
    labels,
    setPageHeader,
    setNavHeader,
    location,
    navigate,
    Routes,
    debounceErrors,
    debounceState,
    isRiccAdministrator,
    cloneDeep,
    setNavCancelFunc,
    dispatch,
  } = useContext(PrivateContext);

  const params = useParams();
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showPopup, setShowPopup] = useState(false);
  const [productData, setProductData] = useState(location?.state);
  const [closeDateCheckbox, setCloseDateCheckbox] = useState(false);
  const [reopenDateCheckbox, setReopenDateCheckbox] = useState(false);

  const productDataFromStore = useSelector(
    (state) => state?.productsReducer?.productDetails
  );

  const [saveProductApi, { isLoading, error }] =
    useSaveProductDetailsMutation();

  const onChange = async (value, state) => {
    debounceState({ ...productData, [state]: value }, setProductData);
    debounceErrors(
      { ...errors, [state]: await validate(value, [state]) },
      setErrors
    );
  };

  const errorProps = (state) => {
    return {
      value: productData[state],
      error: errors[state],
      touched: touched[state],
      touch: () => setTouched({ ...touched, [state]: true }),
    };
  };

  const saveProductDetails = () => {
    const payload = cloneDeep(productData);
    scrollToTop();

    let defaultProductOptionSettings = cloneDeep(
      Constants.defaultProductData.productOptionSettings
    );

    const existingSettingsMap = new Map(
      (payload.productOptionSettings || []).map((opt) => [opt.type, opt])
    );

    defaultProductOptionSettings.forEach((opt) => {
      const type = opt.type;
      const value = productData[type];
      const matchesProductType =
        opt.productType === productData.productType ||
        opt.productType === "ALL";

      if (value === undefined && !matchesProductType) return;

      if (existingSettingsMap.has(type)) {
        existingSettingsMap.get(type).value = value;
      } else {
        const newSetting = cloneDeep(opt);
        newSetting.value = value;
        newSetting.productSponsorId = productData.productSponsorId;
        payload.productOptionSettings.push(newSetting);
      }
    });
    saveProductApi(payload)
      .unwrap()
      .then((res) => {
        if (!res.inError) {
          setShowPopup(true);
        }
      });
  };

  const backToProductList = () => {
    dispatch(setProductDetails({}));
    navigate(Routes.productList.path);
    return;
  };

  useEffect(() => {
    if (productDataFromStore?.name) {
      setProductData(cloneDeep(productDataFromStore));
    } else if (location?.state) {
      let data = location?.state;
      data.externalId = null;
      data.description = "";
      data.name = "";
      data.activeDt = "";
      data.closedDt = "";
      data.newInvestorCloseDt = "";
      data.newPurchaseCloseDt = "";
      data.lastMntDateTime = "";

      data.setupComplete = false;
      data.setupCompleteDt = "";
      data.productId = null;

      data.productOptions?.productOptionSettings?.forEach((e) => {
        e.lastMntDateTime = "";
        e.productId = null;
      });

      if (data.status === "closed") {
        data.closedDt = "";
      }

      setProductData(data);
    }
  }, []);

  useEffect(() => {
    setPageHeader(labels?.duplicateProduct?.title);
    setNavHeader(labels?.navbar?.product);
    setNavCancelFunc(() => backToProductList);
  }, [params]);

  return (
    <ProductContext.Provider
      value={{
        labels,
        onChange,
        errorProps,
        productData,
        setProductData,
        readOnlyAccess: false,
        isRiccAdministrator,
        readOnlyModeForPaymentTableAgeRange: () => false,
        closeAtCheckboxDisabled: true,
        back: () => navigate(-1),
        errors,
        closeDateCheckbox,
        setCloseDateCheckbox,
        reopenDateCheckbox,
        setReopenDateCheckbox,
        disabledActiveDate: false,
        copyProduct: true,
      }}
    >
      {isLoading && <Spinner />}
      <ErrorMessage error={error} />
      {params?.id == 1 ? (
        <ProductPageOne
          next={() => {
            dispatch(setProductDetails(productData));
            navigate(Routes.duplicateProduct.path?.replace(":id", 2), {
              state: productData,
            });
          }}
        />
      ) : params?.id == 2 ? (
        <ProductPageTwo
          next={() => {
            dispatch(setProductDetails(productData));
            navigate(Routes.duplicateProduct.path?.replace(":id", 3), {
              state: productData,
            });
          }}
        />
      ) : params?.id == 3 ? (
        <ProductPageThree submit={saveProductDetails} />
      ) : (
        ""
      )}
      <SuccessPopup
        showModal={showPopup}
        buttonText={labels?.duplicateProduct?.continue}
        click={backToProductList}
        body={labels?.duplicateProduct?.continueMessage}
      />
    </ProductContext.Provider>
  );
};

export default DuplicateProduct;
